"use client";

import { usePricingPlans } from "@zapier/client";
import { Link, SkeletonText, Text } from "@zapier/design-system";
import { useTranslations } from "@zapier/i18n";
import { useEmitShareUpsellEvent } from "@zapier/shared";
import { MONOLITH_BASE_URL } from "@zapier/utils";
import { Icon } from "@zapier/zinnia-icons";
import { useCallback, useMemo } from "react";

import styles from "./TemplateShareUpgradeNudge.module.css";

/** Same utm shape as the repo's other planbuilder upsells; only the campaign differs. */
const UPGRADE_UTM_PARAMS: Record<string, string> = {
  utm_source: "zapier",
  utm_medium: "website",
  utm_campaign: "zpr-gbl-exp-evr-template_share_org_upgrade_nudge-prd",
};

function buildTeamUpgradeUrl(
  teamPlanId: number,
  redirectUrl: string | undefined,
): string {
  const parameters = new URLSearchParams(UPGRADE_UTM_PARAMS);
  parameters.set("planId", String(teamPlanId));
  // Absent only when there's no `location` to return to (non-browser render).
  if (redirectUrl) {
    parameters.set("redirectUrl", redirectUrl);
  }
  return `${MONOLITH_BASE_URL}/app/planbuilder/plans?${parameters.toString()}`;
}

export interface TemplateShareUpgradeNudgeProps {
  templateId: string;
}

/**
 * Stands in for the Organization sharing panel on plans without teams,
 * prompting an upgrade to Team instead.
 */
export function TemplateShareUpgradeNudge({
  templateId,
}: TemplateShareUpgradeNudgeProps) {
  const t = useTranslations("template-share-modal");
  const { pricingPlans, isLoading: isLoadingPlans } = usePricingPlans();
  const { emit } = useEmitShareUpsellEvent();

  const upgradeUrl = useMemo(() => {
    // Plans arrive grouped by type, each group sorted by task allowance
    // ascending, so `plans[0]` is the entry-level Team plan.
    const teamPlanId = pricingPlans?.find((group) => group.planType === "Team")
      ?.plans[0]?.id;
    if (!teamPlanId) return;
    return buildTeamUpgradeUrl(teamPlanId, globalThis.location?.href);
  }, [pricingPlans]);

  const handleUpgradePress = useCallback(() => {
    emit({
      optionSelected: "share_template",
      assetId: templateId,
      interactionRawText: t("upgrade-nudge.cta"),
    });
  }, [emit, templateId, t]);

  return (
    <div
      aria-label={t("upgrade-nudge.region-label")}
      className={styles.root}
      data-testid="template-share-upgrade-nudge"
      role="region"
    >
      <Icon aria-hidden name="actionLock" size={24} />
      <Text
        className={styles.title}
        color="TextDefault"
        tag="p"
        type="Body1Multiline"
      >
        {t("upgrade-nudge.title")}
      </Text>
      {isLoadingPlans ? (
        <SkeletonText lines={1} width="10ch" />
      ) : (
        <Link
          // Disabled rather than pointed at an unfiltered plan picker: without a
          // resolved Team plan the CTA can't honour what the nudge promises.
          isDisabled={!upgradeUrl}
          href={upgradeUrl}
          onPress={handleUpgradePress}
          size="small"
          variant="primary"
        >
          {t("upgrade-nudge.cta")}
        </Link>
      )}
    </div>
  );
}
